import type { MutableRefObject } from 'react';
import React from 'react';
import { isNaN, isNil, omitBy, overSome } from 'lodash';
import { useRouter } from 'next/router';

import { parseQueryObject } from 'Components/Account/AccountPage.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import Pagination from 'Components/Pagination/Pagination';
import type { BasicPaginationMeta } from 'Components/Pagination/Pagination.types';
import { triggerAccountPaginationEvent } from 'Services/analytics/events';
import { useAccountFiltersStore } from 'Stores/AccountFiltersStore/AccountFiltersStore';
import ROUTES from 'Utilities/routes';

type AccountPaginationProps = {
	count?: number;
	scrollToRef?: MutableRefObject<JSX.Element>;
};

const AccountPagination: React.FC<AccountPaginationProps> = ({ count, scrollToRef }) => {
	const { query } = useRouter();
	const { category, slug } = useAccountRouter();

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const { dateRangeType, keyword, purchasesCompleteStatusType } = useAccountFiltersStore((state) => state[category]);

	const storeQuery = omitBy(
		{ category, dateRangeType, keyword, purchasesCompleteStatusType, slug },
		overSome([isNil, isNaN]),
	);

	const params = parseQueryObject(query);

	const pages = params?.limit !== undefined && count ? Math.ceil(count / Number(params?.limit)) : 0;

	const meta: BasicPaginationMeta = {
		itemsPerPage: params?.limit || 0,
		pageNumber: Number(query?.page) || 1,
		pages,
		totalItemsCount: count || 0,
	};

	const paginationAnalyticsEventHandler = (pageNumber: number) => {
		triggerAccountPaginationEvent(slug, String(pageNumber));
	};

	return (
		<Pagination
			analyticsEventHandler={paginationAnalyticsEventHandler}
			meta={meta}
			otherQueryParams={{ ...query, ...storeQuery }}
			pathName={ROUTES.ACCOUNT.href}
			scrollToRef={scrollToRef}
		/>
	);
};

export default AccountPagination;
